import { store } from '../redux/store';
import { clearAuth, storeAppAuth } from '../redux/reducers/authSlice';
import { storeUserInfo } from '../redux/reducers/userSlice';
import { AxiosResponse } from 'axios';
import { decodeJwtToken } from '../utils/decodeJwtToken';
import {
  clearLocalData,
  getLocalAccessToken,
  getLocalRefreshTokenExpiry,
  setLocalAccessToken,
  setLocalRefreshToken,
  setLocalRefreshTokenExpiry,
  setLocalUser,
} from '../utils/localStorageHandler';
import { SignInFormData } from '../types/interfaces/auth.interfaces';
import { showToast } from './toast.service';
import { appContextGet } from '../api/httpCommon/appAxios.service';
import { refreshToken, signIn } from '../api/authApi';
import { AUTH_API_ENDPOINTS } from '../constants/apiEndpoints';
import { toastMessages } from '../constants/errorMessages';
import { ROUTE_PATHS } from '../constants/routePaths';
import { localStorageConstants } from '../constants/localStorage.constants';

export const signInUser = async (formData: SignInFormData, rememberMe: boolean) => {
  try {
    const { email } = formData;
    const { data } = await signIn(formData);
    const { sub: userId } = data?.token && decodeJwtToken(data.token);
    clearLocalData();

    if (data?.refreshToken && data?.refreshTokenExpiry && data?.token) {
      setLocalAccessToken(data.token, rememberMe);
      setLocalUser(userId, email, rememberMe);
      setLocalRefreshToken(data.refreshToken, rememberMe);
      setLocalRefreshTokenExpiry(data.refreshTokenExpiry, rememberMe);
    }
    store.dispatch(storeAppAuth(data));
    store.dispatch(storeUserInfo({ userId }));
  } catch (err: any) {
    throw err;
  }
};

export const getAccountContextById = (params: { accountId: number }): Promise<AxiosResponse<{}, any>> => {
  return appContextGet(AUTH_API_ENDPOINTS.ACCOUNT, params);
};

export const signOutUser = () => {
  try {
    store.dispatch(clearAuth());
    clearLocalData();
    window.location.href = ROUTE_PATHS.SIGN_IN;
  } catch {
    showToast.error(toastMessages.SOMETHING_WENT_WRONG);
  }
};

export const handleUnauthorizedUser = () => {
  if (isAccessTokenExpired()) {
    if (isRefreshTokenExpired()) {
      showToast.error(toastMessages.SESSION_EXPIRED);
      signOutUser();
    } else {
      refreshAccessToken();
    }
  }
};

const refreshAccessToken = async () => {
  try {
    const { data } = await refreshToken();
    if (data?.token && data?.refreshToken && data?.refreshTokenExpiry) {
      const isStaySignedIn = !!localStorage.getItem(localStorageConstants.USER);
      setLocalAccessToken(data.token, isStaySignedIn);
      setLocalRefreshToken(data.refreshToken, isStaySignedIn);
      store.dispatch(storeAppAuth(data));
    } else {
      signOutUser();
    }
  } catch (err: any) {
    signOutUser();
    throw err;
  }
};

const isAccessTokenExpired = () => {
  const accessToken = getLocalAccessToken();
  if (accessToken) {
    const { exp } = decodeJwtToken(accessToken);
    const expiry = new Date(exp * 1000);
    return expiry <= new Date();
  }
  return true;
};

const isRefreshTokenExpired = () => {
  const refreshTokenExpiry = getLocalRefreshTokenExpiry();
  if (refreshTokenExpiry) {
    const expiry = new Date(refreshTokenExpiry);
    return expiry <= new Date();
  }
  return true;
};
